<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop. -->
    <!-- On mobile, it's ml-0 (full width), allowing the sidebar to overlap it. -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky, stays at top of this wrapper) -->
      <header class="sticky top-0 z-20 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
          @change-lang="changeLang"
          @logout="logout"
        />
      </header>

      <!-- Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Page Header -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Dashboard Overview</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Last updated: {{ currentDate }}</p>
          </div>
          <button 
            @click="refreshData"
            class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76] dark:focus:ring-offset-gray-900"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" :class="{'animate-spin': isRefreshing}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          <!-- Total Staff -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Staff</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ totalStaff }}</p>
              </div>
              <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">All teaching and non-teaching staff</p>
          </div>

          <!-- Teaching Staff -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Teaching Staff</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ teachingStaff }}</p>
                <div class="flex flex-wrap gap-2 mt-3">
                  <span class="text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-md">N: {{ staffBySection.nursery }}</span>
                  <span class="text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-md">P: {{ staffBySection.primary }}</span>
                  <span class="text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-1 rounded-md">S: {{ staffBySection.secondary }}</span>
                </div>
              </div>
              <div class="p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">N=Nursery, P=Primary, S=Secondary</p>
          </div>

          <!-- Outsourced Staff -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Outsourced Staff</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ outsourcedStaff }}</p>
                <div class="flex flex-wrap gap-2 mt-3">
                  <span class="text-xs font-medium bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded-md">Security: {{ outsourcedByType.security }}</span>
                  <span class="text-xs font-medium bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-1 rounded-md">Cleaning: {{ outsourcedByType.cleaning }}</span>
                </div>
              </div>
              <div class="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Contracted external personnel</p>
          </div>

          <!-- Extracurricular Staff -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Extracurricular</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ extracurricularStaff }}</p>
                <div class="flex flex-wrap gap-2 mt-3">
                  <span class="text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 px-2 py-1 rounded-md">Sports: {{ extracurricularByType.sports }}</span>
                  <span class="text-xs font-medium bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400 px-2 py-1 rounded-md">Arts: {{ extracurricularByType.arts }}</span>
                </div>
              </div>
              <div class="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Part-time specialized instructors</p>
          </div>
        </div>

        <!-- Charts Section -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          <!-- Staff Distribution Chart -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Staff Distribution</h2>
              <select 
                v-model="selectedPeriod"
                class="text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg focus:ring-[#035a76] focus:border-[#035a76] block w-full sm:w-auto px-3 py-2"
              >
                <option value="month">This Month</option>
                <option value="year">This Year</option>
                <option value="all">All Time</option>
              </select>
            </div>
            <div id="staffChart" class="w-full"></div>
          </div>

          <!-- Staff Composition Chart -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Staff Composition</h2>
              <select 
                v-model="selectedPeriod"
                class="text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg focus:ring-[#035a76] focus:border-[#035a76] block w-full sm:w-auto px-3 py-2"
              >
                <option value="month">This Month</option>
                <option value="year">This Year</option>
                <option value="all">All Time</option>
              </select>
            </div>
            <div id="compositionChart" class="w-full"></div>
          </div>
        </div>

        <!-- Recent Activity Section -->
        <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Recent HR Activity</h2>
            <button class="text-sm font-medium text-[#035a76] dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">View All</button>
          </div>
          <div class="space-y-6">
            <div v-for="(activity, index) in recentActivities" :key="index" class="flex items-start gap-4">
              <div :class="`p-2.5 rounded-full flex-shrink-0 ${activity.color}`">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path :stroke-linecap="activity.icon.cap" :stroke-linejoin="activity.icon.join" :stroke-width="2" :d="activity.icon.path" />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ activity.title }}</p>
                <p class="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{{ activity.description }}</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">{{ activity.time }}</p>
              </div>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import ApexCharts from 'apexcharts';
import HrSidebar from '../../components/hr/HrSidebar.vue';
import HrTopbar from '../../components/hr/HrTopbar.vue';

const currentPageTitle = ref('HR Dashboard');
const isSidebarOpen = ref(true);
const isDarkMode = ref(false);
const isRefreshing = ref(false);
const selectedPeriod = ref('month');

// Staff data
const totalStaff = ref(125);
const teachingStaff = ref(85);
const nonTeachingStaff = ref(40);
const outsourcedStaff = ref(15);
const extracurricularStaff = ref(10);

const staffBySection = ref({ nursery: 20, primary: 35, secondary: 30 });
const outsourcedByType = ref({ security: 8, cleaning: 5, maintenance: 2 });
const extracurricularByType = ref({ sports: 6, arts: 4 });

const totalCalculated = computed(() => teachingStaff.value + nonTeachingStaff.value);

const recentActivities = ref([
  {
    title: 'New staff onboarded',
    description: 'John Doe joined as Primary Section Teacher',
    time: '2 hours ago',
    color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    icon: { path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', cap: 'round', join: 'round' }
  },
  {
    title: 'Leave request submitted',
    description: 'Sarah Smith requested leave from 15-20 June',
    time: '1 day ago',
    color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    icon: { path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', cap: 'round', join: 'round' }
  },
  {
    title: 'Contract ended',
    description: 'Outsourced security guard Michael completed contract',
    time: '3 days ago',
    color: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    icon: { path: 'M6 18L18 6M6 6l12 12', cap: 'round', join: 'round' }
  }
]);

const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

let staffChart = null;
let compositionChart = null;

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.theme = 'light';
  }
  renderCharts(); // Re-render charts to update colors
};

const refreshData = () => {
  isRefreshing.value = true;
  setTimeout(() => {
    console.log('Refreshing HR data...');
    isRefreshing.value = false;
    renderCharts();
  }, 800);
};

const renderCharts = () => {
  if (staffChart) staffChart.destroy();
  if (compositionChart) compositionChart.destroy();

  const isDark = isDarkMode.value;
  const textColor = isDark ? '#9ca3af' : '#6b7280';
  const gridColor = isDark ? '#374151' : '#e5e7eb';
  const chartBg = isDark ? '#1f2937' : '#ffffff';

  const staffOptions = {
    series: [{
      name: 'Staff Count',
      data: [staffBySection.value.nursery, staffBySection.value.primary, staffBySection.value.secondary, nonTeachingStaff.value, outsourcedStaff.value, extracurricularStaff.value]
    }],
    chart: {
      type: 'bar',
      height: 350,
      toolbar: { show: false },
      background: 'transparent',
      foreColor: textColor
    },
    colors: ['#facc15', '#e11d48', '#22c55e', '#035a76', '#ef4444', '#6366f1'],
    plotOptions: {
      bar: { borderRadius: 6, horizontal: false, distributed: true, columnWidth: '60%' }
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: ['Nursery', 'Primary', 'Secondary', 'Non-Teaching', 'Outsourced', 'Extracurricular'],
      labels: { style: { colors: textColor, fontSize: '12px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: { style: { colors: textColor } }
    },
    grid: { borderColor: gridColor, strokeDashArray: 4 },
    tooltip: { theme: isDark ? 'dark' : 'light' }
  };

  const compositionOptions = {
    series: [teachingStaff.value, nonTeachingStaff.value, outsourcedStaff.value, extracurricularStaff.value],
    chart: {
      type: 'donut',
      height: 350,
      background: 'transparent',
      foreColor: textColor
    },
    colors: ['#3b82f6', '#22c55e', '#eab308', '#a855f7'],
    labels: ['Teaching', 'Non-Teaching', 'Outsourced', 'Extracurricular'],
    legend: { position: 'bottom', labels: { colors: textColor } },
    dataLabels: {
      enabled: true,
      formatter: (val) => Math.round(val) + "%",
      style: { colors: [isDark ? '#fff' : '#000'], fontSize: '14px', fontWeight: 600 }
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              color: textColor,
              formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0)
            }
          }
        }
      }
    },
    tooltip: { theme: isDark ? 'dark' : 'light' },
    responsive: [{
      breakpoint: 480,
      options: { chart: { width: '100%' }, legend: { position: 'bottom' } }
    }]
  };

  staffChart = new ApexCharts(document.querySelector("#staffChart"), staffOptions);
  staffChart.render();

  compositionChart = new ApexCharts(document.querySelector("#compositionChart"), compositionOptions);
  compositionChart.render();
};

onMounted(() => {
  renderCharts();
  if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDarkMode.value = true;
    document.documentElement.classList.add('dark');
  }
});

watch([selectedPeriod, isDarkMode], () => { renderCharts(); }, { deep: true });
watch([teachingStaff, nonTeachingStaff], () => { totalStaff.value = teachingStaff.value + nonTeachingStaff.value; });
</script>

<style scoped>
/* Smooth transitions for dark mode elements */
.bg-white, .bg-gray-50, .bg-gray-800, .bg-gray-900 {
  transition: background-color 0.3s ease;
}
.text-gray-900, .text-gray-500, .text-gray-400 {
  transition: color 0.3s ease;
}
.border-gray-100, .border-gray-200, .border-gray-300, .border-gray-700 {
  transition: border-color 0.3s ease;
}
</style>